import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Users, FileText, Shield, TrendingUp, ChevronRight, Map as MapIcon } from 'lucide-react';
import { toast } from 'sonner';
import { computeDashboardTotals } from '../utils/visitCalculations';
import { Button } from '../components/ui/button';
import { DashboardMap } from '../components/DashboardMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1a365d', '#234876', '#2b5a8f', '#346ca8', '#3d7ec1', '#4690da', '#5ba3e8', '#70b6f0', '#85c9f8', '#9adcff'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [agencyData, setAgencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, topAgenciesRes, visitsRes, configRes, projectionsRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/dashboard/top-agencies`),
        axios.get(`${API}/visits`, { headers }),
        axios.get(`${API}/tracking-config`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/projections`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      const dashStats = statsRes.data;
      const trackingConfig = configRes.data;
      const visits = visitsRes.data || [];
      const projections = projectionsRes.data || [];
      
      // Use shared utility — same function used by Therapist & Agency Reports
      const totals = computeDashboardTotals(visits, projections, trackingConfig);
      dashStats.last_upload_visits = totals.finalCount;
      dashStats.projected_visits = totals.projectedCount;
      dashStats.last_saturday_date = totals.finalLabel;
      dashStats.next_saturday_date = totals.projLabel;
      
      setStats(dashStats);
      
      const sortedAgencies = topAgenciesRes.data
        .map((item) => ({
          id: item.id,
          name: item.agency_name.length > 20 
            ? item.agency_name.substring(0, 20) + '...' 
            : item.agency_name,
          fullName: item.agency_name,
          value: item.last_upload_visits,
        }));
      
      setAgencyData(sortedAgencies);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  const metricCards = [
    {
      title: 'Active Staff',
      value: stats?.total_profiles || 0,
      icon: Users,
      color: 'text-sage',
      bgColor: 'bg-sage/10',
      testId: 'total-profiles',
      link: '/personnel',
    },
    {
      title: `Final (${stats?.last_saturday_date || ''})`,
      value: stats?.last_upload_visits || 0,
      icon: FileText,
      color: 'text-clay',
      bgColor: 'bg-clay/10',
      testId: 'last-upload-visits',
      link: '/visit-history?metric=final',
    },
    {
      title: `Projected (${stats?.next_saturday_date || ''})`,
      value: stats?.projected_visits || 0,
      icon: TrendingUp,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      testId: 'projected-visits',
      link: '/visit-history?metric=projected',
    },
    {
      title: 'Active Agencies',
      value: stats?.active_agencies || 0,
      icon: Shield,
      color: 'text-stone',
      bgColor: 'bg-stone/10',
      testId: 'active-agencies',
      link: '/agencies',
    },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Overview of your patient visit tracking system
          </p>
        </div>
        <Button
          onClick={() => setShowMap((v) => !v)}
          className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
          data-testid="toggle-map-view-btn"
        >
          <MapIcon size={18} className="mr-2" />
          {showMap ? 'Hide Map' : 'Map View'}
        </Button>
      </div>

      {showMap && (
        <Card className="p-4" data-testid="dashboard-map-panel">
          <DashboardMap />
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card
              key={metric.title}
              className="metric-card p-6 cursor-pointer hover:shadow-lg transition-shadow"
              data-testid={metric.testId}
              onClick={() => navigate(metric.link)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    {metric.title}
                  </p>
                  <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                    {metric.value}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${metric.bgColor}`}>
                  <Icon className={metric.color} size={24} strokeWidth={1.5} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {agencyData.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading text-xl font-semibold">
                Top 10 Agencies (Last Upload)
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Click on an agency to view details
              </p>
            </div>
            <button
              onClick={() => navigate('/agencies')}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              View all agencies
              <ChevronRight size={16} />
            </button>
          </div>
          
          <div className="space-y-2">
            {agencyData.map((agency, index) => {
              const maxValue = Math.max(...agencyData.map(a => a.value));
              const barWidth = (agency.value / maxValue) * 100;
              return (
                <div
                  key={agency.id || index}
                  onClick={() => navigate(`/agencies/${agency.id}`)}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group"
                  data-testid={`agency-row-${index}`}
                >
                  <span className="w-40 text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {agency.fullName}
                  </span>
                  <div className="flex-1 h-8 bg-muted/30 rounded overflow-hidden">
                    <div
                      className="h-full rounded transition-all duration-300 group-hover:opacity-80"
                      style={{ 
                        width: `${barWidth}%`,
                        backgroundColor: COLORS[index % COLORS.length]
                      }}
                    />
                  </div>
                  <span className="w-12 text-right font-bold tabular-nums text-foreground">
                    {agency.value}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {agencyData.length === 0 && !loading && (
        <Card className="p-8 text-center">
          <Shield className="mx-auto text-muted-foreground mb-4" size={48} />
          <h3 className="font-heading text-lg font-semibold mb-2">No Agency Data Yet</h3>
          <p className="text-muted-foreground">
            Upload visit data to see agency statistics here.
          </p>
        </Card>
      )}
    </div>
  );
}
